import React from 'react';
import { useTranslation } from 'react-i18next';
import { TCN } from '../ui-kit/tokens';
import Icon from '../ui-kit/Icon';

/**
 * TCN TabBar — sticky bottom 5-tab nav, iOS-flavored.
 * Backdrop blur + rgba background, padding-bottom 32 (safe area).
 *
 * Tabs (CLAUDE_TCN.md §1):
 *   today (M1) · creators (M4) · tiers (M3) · payout (M5) · actions (M6)
 *
 * Props:
 *  - active: tab id
 *  - onChange: (id) => void
 */
export const TCN_TABS = [
  { id: 'today',    icon: 'home',  i18n: 'tcn.tabs.today',    fallback: 'Today' },
  { id: 'creators', icon: 'people', i18n: 'tcn.tabs.creators', fallback: 'Creators' },
  { id: 'tiers',    icon: 'tiers', i18n: 'tcn.tabs.tiers',    fallback: 'Tiers' },
  { id: 'payout',   icon: 'cash',  i18n: 'tcn.tabs.payout',   fallback: 'Pay-out' },
  { id: 'actions',  icon: 'check', i18n: 'tcn.tabs.actions',  fallback: 'Actions' },
];

const TabBar = ({ active, onChange, className = '', style }) => {
  const { t } = useTranslation();

  return (
    <nav
      role="tablist"
      className={`sticky bottom-0 left-0 right-0 ${className}`}
      style={{
        background: 'rgba(10,10,11,0.92)',
        backdropFilter: 'blur(20px) saturate(140%)',
        WebkitBackdropFilter: 'blur(20px) saturate(140%)',
        borderTop: `1px solid ${TCN.border}`,
        paddingBottom: 'calc(32px + env(safe-area-inset-bottom, 0))',
        paddingTop: 8,
        zIndex: 50,
        flexShrink: 0,
        ...style,
      }}
    >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${TCN_TABS.length}, 1fr)`,
          alignItems: 'center',
        }}
      >
        {TCN_TABS.map((tab) => {
          const isActive = active === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => onChange?.(tab.id)}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 3,
                padding: '6px 4px',
                background: 'transparent',
                border: 'none',
                // Mockup ui-kit.jsx:79 — active = text (white), inactive = textMute, no pill/underline
                color: isActive ? TCN.text : TCN.textMute,
                fontFamily: TCN.fontSans,
                fontSize: 10,
                fontWeight: 600,
                letterSpacing: '0.02em',
                cursor: 'pointer',
                transition: `color ${TCN.durBase} ${TCN.ease}`,
              }}
            >
              {/* Mockup uses identical strokeWidth (1.8) active vs inactive */}
              <Icon name={tab.icon} size={22} strokeWidth={1.8} />
              <span>{t(tab.i18n, tab.fallback)}</span>
            </button>
          );
        })}
      </div>
    </nav>
  );
};

export default TabBar;
